import { ArrowRight } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { EmbeddedPage } from '../lib/scopedParams';
import { Callout } from './blocks';
import { NavTabs } from './NavTabs';

export interface ObjectTab {
  id: string;
  label: string;
  /** The tab's content. */
  content: ReactNode;
  /**
   * The content is a reused list or settings page: it is shown as a section, its own query
   * parameters live next to `tab`, and `fixed` parameters (e.g. this registry) are always set.
   */
  embedded?: boolean;
  fixed?: Record<string, string>;
}

/** The open tab of an object page: `?tab=`, or the first tab. */
export function useObjectTab(ids: readonly string[]): string {
  const [params] = useSearchParams();
  const requested = params.get('tab') ?? '';
  return ids.includes(requested) ? requested : (ids[0] ?? '');
}

/**
 * Tabs of an object page (a TLD, a registry): the tab bar and the open tab. The first tab is the
 * page's own URL; the others add `?tab=<id>`, so links and the back button keep the tab.
 */
export function ObjectTabs({ base, label, tabs }: { base: string; label: string; tabs: readonly ObjectTab[] }) {
  const active = useObjectTab(tabs.map((x) => x.id));
  const tab = tabs.find((x) => x.id === active);
  return (
    <>
      <NavTabs label={label} active={active} tabs={tabs.map((x, i) => ({ id: x.id, label: x.label, to: i === 0 ? base : `${base}?tab=${encodeURIComponent(x.id)}` }))} />
      {tab?.embedded ? (
        <EmbeddedPage key={tab.id} reserved={['tab']} fixed={tab.fixed} label={tab.label}>
          {tab.content}
        </EmbeddedPage>
      ) : (
        tab?.content
      )}
    </>
  );
}

/** A note that a setting lives in the Settings hub, with a link to the same form there. */
export function SettingsLink({ title, body, to, action }: { title: string; body: string; to: string; action: string }) {
  return (
    <Callout tone="info" title={title} className="mb-5">
      <p>{body}</p>
      <Button asChild variant="outline" size="sm" className="mt-2">
        <Link to={to}>
          {action} <ArrowRight />
        </Link>
      </Button>
    </Callout>
  );
}
